import React, { createContext, useContext, useState, useEffect } from 'react';
import { useCompany } from './CompanyContext';

const DEFAULTS = {
  app_name:        'Vocaverse',
  app_logo_url:    '/logo.png',
  home_logo_url:   '/placeholder.svg',
  home_hero_title: 'Welcome to My Blog',
  home_hero_body:  'Share your stories, insights and updates with the world.',
  tile_1_label:    'Link 1',
  tile_1_url:      '#',
  tile_1_img:      '/placeholder.svg',
  tile_2_label:    'Link 2',
  tile_2_url:      '#',
  tile_2_img:      '/placeholder.svg',
  tile_3_label:    'Link 3',
  tile_3_url:      '#',
  tile_3_img:      '/placeholder.svg',
  footer_org_name: 'Vocaverse',
  footer_tagline:  'Sharing knowledge and inspiring our community.',
  footer_address:  '',
  footer_phone:    '',
  footer_email:    '',
  footer_map_url:  'https://maps.google.com',
  footer_map_img:  '/placeholder.svg',
  footer_layout:   'business',
  footer_custom_title: '',
  footer_custom_line_1: '',
  footer_custom_line_2: '',
  footer_custom_line_3: '',
  footer_custom_line_4: '',
  footer_custom_show_1: 'false',
  footer_custom_show_2: 'false',
  footer_custom_show_3: 'false',
  footer_custom_show_4: 'false',
  social_tiktok_url:   '',
  social_instagram_url:'',
  social_x_url:        '',
  social_linkedin_url: '',
  social_facebook_url: '',
  navigation:      JSON.stringify([
    { id: 'home', type: 'system', label: 'Home', url: '/', children: [] },
    { id: 'links', type: 'system', label: 'Links', url: '/links', children: [] },
    { id: 'blog', type: 'system', label: 'Blog', url: '/blog', children: [] },
    { id: 'community', type: 'system', label: 'Community', url: '/community', children: [] }
  ]),
  brand_font:          'System Default',
  brand_primary:       '#4f46e5',
  brand_primary_dark:  '#4338ca',
  brand_primary_light: '#eef2ff',
  brand_accent:        '#6366f1',
  brand_nav_bg:        '#ffffff',
  brand_footer_bg:     '#111827',
  brand_footer_text:   '#ffffff',
  body_background:     '#ffffff',
  body_background_css: '',
  heading_text:        '#1f112e',
  body_text:           '#3d3447',
  muted_text:          '#5a4f69',
  theme_template:      'Original',
  custom_templates:    '[]',
};

const LEGACY_IMAGE_URLS = new Set([
  'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1501504905252-473c47e087f8?q=80&w=2074&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=2074&auto=format&fit=crop',
  'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1782306864549-image.png',
  'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1782307513753-image.png',
]);

const IMAGE_KEYS = new Set(['app_logo_url', 'home_logo_url', 'tile_1_img', 'tile_2_img', 'tile_3_img', 'footer_map_img']);

const LEGACY_NAMES = new Set(['', null, 'My Blog', 'Haringey Learns']);

const sanitizeName = (value, fallback) => {
  if (!value || LEGACY_NAMES.has(value)) return fallback;
  return value;
};

const isLegacyImage = (url) => !url || LEGACY_IMAGE_URLS.has(url);

const mergeSettings = (data, base) => {
  const merged = { ...base };
  for (const [key, value] of Object.entries(data.settings || {})) {
    if (value === '' || value == null) continue;
    if (IMAGE_KEYS.has(key) && isLegacyImage(value)) continue;
    merged[key] = value;
  }
  return merged;
};

const SettingsContext = createContext({ settings: DEFAULTS, globalSettings: DEFAULTS, reload: () => {} });

export const SettingsProvider = ({ children }) => {
  const { company } = useCompany();
  const blogId = company?.id || null;
  const [settings, setSettings] = useState(DEFAULTS);
  const [globalSettings, setGlobalSettings] = useState(DEFAULTS);

  const applyBrandVars = (s) => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary',       s.brand_primary       || '#4f46e5');
    root.style.setProperty('--brand-primary-dark',  s.brand_primary_dark  || '#4338ca');
    root.style.setProperty('--brand-primary-light', s.brand_primary_light || '#eef2ff');
    root.style.setProperty('--brand-accent',        s.brand_accent        || '#6366f1');
    root.style.setProperty('--brand-nav-bg',        s.brand_nav_bg        || '#ffffff');
    root.style.setProperty('--brand-footer-bg',     s.brand_footer_bg     || '#111827');
    root.style.setProperty('--brand-footer-text',   s.brand_footer_text   || '#ffffff');
    const bodyBg = s.body_background_css?.trim() ? s.body_background_css.trim() : (s.body_background || '#ffffff');
    root.style.setProperty('--brand-body-bg',       bodyBg);
    root.style.setProperty('--brand-heading-text',  s.heading_text  || '#1f112e');
    root.style.setProperty('--brand-body-text',     s.body_text     || '#3d3447');
    root.style.setProperty('--brand-muted-text',    s.muted_text    || '#5a4f69');

    const fontName = s.brand_font || '';
    if (fontName && fontName !== 'System Default') {
      const linkId = 'brand-google-font';
      let link = document.getElementById(linkId);
      const encoded = encodeURIComponent(fontName);
      const href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700&display=swap`;
      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      if (link.href !== href) link.href = href;
      root.style.setProperty('--brand-font', `"${fontName}", ui-sans-serif, system-ui, sans-serif`);
    } else {
      root.style.setProperty('--brand-font', 'ui-sans-serif, system-ui, sans-serif');
    }
  };

  const load = async () => {
    try {
      const [globalRes, workspaceRes] = await Promise.all([
        fetch('/api/settings'),
        blogId ? fetch(`/api/settings?blog_id=${encodeURIComponent(blogId)}`) : Promise.resolve(null),
      ]);

      let globalMerged = { ...DEFAULTS };
      if (globalRes.ok) {
        const globalData = await globalRes.json();
        globalMerged = mergeSettings(globalData, DEFAULTS);
        globalMerged.app_name = sanitizeName(globalMerged.app_name, DEFAULTS.app_name);
        globalMerged.footer_org_name = sanitizeName(globalMerged.footer_org_name, DEFAULTS.footer_org_name);
      }
      setGlobalSettings(globalMerged);

      let effective;
      if (workspaceRes?.ok) {
        const workspaceData = await workspaceRes.json();
        const wsMerged = mergeSettings(workspaceData, DEFAULTS);
        if (!wsMerged.app_name || LEGACY_NAMES.has(wsMerged.app_name)) {
          wsMerged.app_name = company?.name || DEFAULTS.app_name;
        }
        if (!wsMerged.footer_org_name || LEGACY_NAMES.has(wsMerged.footer_org_name)) {
          wsMerged.footer_org_name = company?.name || DEFAULTS.app_name;
        }
        effective = wsMerged;
      } else {
        effective = globalMerged;
      }

      setSettings(effective);
      applyBrandVars(effective);
    } catch {
      // Network error — keep defaults, app still works perfectly
      applyBrandVars(DEFAULTS);
    }
  };

  useEffect(() => { load(); }, [blogId]);

  return (
    <SettingsContext.Provider value={{ settings, globalSettings, reload: load }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
